"use client"

import { useState, useEffect } from "react"
import { useParams, Link, useSearchParams, Navigate, useNavigate } from "react-router-dom"
import { Typography, Row, Col, Card, Button, Select, Pagination, Empty, Breadcrumb, Spin } from "antd"
import { ShoppingCartOutlined, HeartOutlined } from "@ant-design/icons"
import axios from "axios"; 
import { motion } from "framer-motion"
import { useCart } from "../contexts/CartContext"
import "../styles/product-cards.css"
import "./ProductListPage.css"
import { apiRequest } from "../utils/apiRequest"
import { useCurrency } from "../contexts/CurrencyContext"
import Seo, { siteBase } from "../components/Seo"
import { buildItemListJsonLd, buildBreadcrumbJsonLd, slugifyCategory } from "../lib/seo"
import useCategoryBySlug, { isCategoryAsProduct, buyPathForCategory } from "../hooks/useCategoryBySlug"
import { LANDING_COPY } from "../lib/landingCopy"
import { assetUrl } from "../lib/assetUrl"

const { Title, Text } = Typography
const { Option } = Select

// SEO copy keyed by the URL slug for network/category landing pages. Falls back to a
// generic template for any slug not listed here, so new categories still get sensible
// titles/descriptions without a code change.

const slugToWords = (slug) =>
  slug
    ? slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : ""

const ProductListPage = () => {
  const { category } = useParams()
  const { addToCart } = useCart()
  const navigate = useNavigate()
  const { formatMoney, formatRange } = useCurrency()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortOrder, setSortOrder] = useState("default")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const [total,setTotal] = useState(1)
  const [searchParams, setSearchParams] = useSearchParams();

  // Resolve the /products/:category slug (e.g. "econet") to its category record.
  // Shared with the /buy/:slug route — see hooks/useCategoryBySlug.
  const { category: resolvedCategory, section: resolvedSection } = useCategoryBySlug(category)
  const resolvedCategoryId = resolvedCategory?.id ?? null

  // Category id can come from ?category=<id> (existing) or the slug route.
  const id = searchParams.get('category') || resolvedCategoryId;

  useEffect(() => {
    
    const fetchProducts = async (page = currentPage) => {
      setLoading(true)

      // A nav-section slug (e.g. "telecel") is not a category, so /products has
      // nothing to filter on — asking it anyway returned the whole catalogue.
      // The section endpoint resolves the taxonomy's own match rules instead.
      if (resolvedSection) {
        const res = await apiRequest({
          method: "GET",
          url: `/nav_sections/${encodeURIComponent(resolvedSection.slug)}/items?limit=${pageSize || 24}`,
          suppressNotifications: true,
        })
        const items = res.data?.items || []
        setProducts(items.map((item) => ({
          id: item.id,
          title: item.name,
          price: item.price,
          icon: assetUrl(item.logo),
          color: 'orange',
          category_id: item.category_id,
          itemType: item.item_type,
          // A category-as-product is bought at /buy/<slug>: its id is a CATEGORY
          // id, so /product/<id> would resolve to a different item entirely.
          href: item.item_type === 'category'
            ? `/buy/${slugifyCategory(item.name)}`
            : `/product/${item.id}?category=${item.category_id}`,
        })))
        setTotal(items.length)
        setLoading(false)
        return
      }

      let url = `/products`;
    
      // Create URLSearchParams for all parameters
      const params = new URLSearchParams();

      if(id){params.append('category', id)}

      if (pageSize && !isNaN(pageSize) && pageSize > 0) {
        params.append('limit', pageSize);
      }

       if(sortOrder){
          params.append('sortorder', sortOrder);
        }

      if (page) {
        params.append('page', page);
      }

      url += `?${params.toString()}`;

      const response = await apiRequest({
        method: "GET",
        url,
      })

      const p = await Promise.all(
        response.data.products.map(async (product) => {
  
          return {
            id: product.id,
            code: product.productCode,
            title: product.name,
            price: product.price,
            icon: assetUrl(product.logo),
            color: 'orange',
            category_id: product.category_id
          };
        })
      );

      setTotal(response.data.total)
      setPageSize(response.data.limit)
      setProducts(p)

      setLoading(false)
    }

    fetchProducts()
  }, [currentPage, sortOrder, id, resolvedSection])

  const handleSortChange = (value) => {
    setSortOrder(value)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo(0, 0)
  }

  const formatPrice = (price) => {
    if (Array.isArray(price)) return formatRange(price)
    return formatMoney(price)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.05,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
  }

  const getCategoryTitle = () => {
    if (!category) return "Products"
    return slugToWords(category)
  }

  // SEO: landing copy for the current network/category slug (or a generic default).
  const landing = category ? LANDING_COPY[category.toLowerCase()] : null
  const pageTitle = landing?.title || (category ? `${getCategoryTitle()} — Buy Online` : "Airtime, Data Bundles & ZESA Tokens for Zimbabwe")
  const pageDescription =
    landing?.intro ||
    (category
      ? `Buy ${getCategoryTitle()} online with Tanaka Life — instant delivery to Zimbabwe from South Africa and the diaspora.`
      : "Buy instant airtime, data bundles and ZESA tokens to Zimbabwe from South Africa and the diaspora. Secure payment by voucher, card or instant EFT.")
  const canonical = category ? `${siteBase}/products/${category}` : `${siteBase}/products`
  const itemListJsonLd = buildItemListJsonLd(
    products.map((p) => ({ name: p.title, url: `/product/${p.id}?category=${p.category_id}` })),
    siteBase,
  )
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(
    [
      { name: "Home", url: "/" },
      { name: "Products", url: "/products" },
      ...(category ? [{ name: getCategoryTitle(), url: `/products/${category}` }] : []),
    ],
    siteBase,
  )

  // A category with no child products IS the sellable item (isconv) — this listing page
  // would render an empty grid for it. Send the visitor to the page that actually sells
  // it, so old links and search results don't dead-end and signals consolidate on one URL.
  if (isCategoryAsProduct(resolvedCategory)) {
    return <Navigate to={buyPathForCategory(resolvedCategory)} replace />
  }

  return (
    <div className="product-list-page">
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonical={canonical}
        keywords={`${getCategoryTitle()} Zimbabwe, buy ${getCategoryTitle().toLowerCase()}, airtime Zimbabwe, data bundles Zimbabwe, ZESA tokens, send airtime to Zimbabwe from South Africa`}
        jsonLd={[itemListJsonLd, breadcrumbJsonLd]}
      />
      <div className="product-list-container">
        <Breadcrumb className="breadcrumb">
          <Breadcrumb.Item>
            <Link to="/">Home</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/categories">Categories</Link>
          </Breadcrumb.Item>
          {category && <Breadcrumb.Item>{getCategoryTitle()}</Breadcrumb.Item>}
        </Breadcrumb>

        <div className="product-list-header">
          <Title level={2}>{getCategoryTitle()}</Title>

          {landing?.intro && (
            <Text type="secondary" style={{ display: "block", maxWidth: 760, marginBottom: 8 }}>
              {landing.intro}
            </Text>
          )}

          <div className="product-list-actions">
            <Text>Showing all {products.length} results</Text>
            <Select defaultValue="default" onChange={handleSortChange} className="sort-select">
              <Option value="default">Default sorting</Option>
              <Option value="asc">Sort by price: low to high</Option>
              <Option value="desc">Sort by price: high to low</Option>
            </Select>
          </div>
        </div>

        {/*
          Switching pages used to swap the whole grid for a bare spinner: the
          content collapsed to ~40px, the page reflowed underneath the click,
          and Pagination stayed enabled and unstyled throughout — so a page
          change gave no feedback where the user was actually looking and felt
          like the click hadn't registered, especially on a slow connection.

          Spin now overlays the existing cards instead of replacing them, so
          something recognisable stays on screen at a stable height and the
          spinner appears right where the user is looking. Pagination is
          disabled for the same window so a slow load can't be clicked twice.
        */}
        <Spin spinning={loading} size="large" tip={products.length ? "Loading..." : undefined}>
          {products.length > 0 ? (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <Row gutter={[24, 24]}>
                {products.map((product) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={`${product.itemType || 'product'}-${product.id}`}>
                    <motion.div variants={itemVariants}>
                      <Card
                        hoverable
                        className="product-card"
                        size="small"
                        cover={
                          <img src={product.icon} alt={product.title} loading="lazy" />
                        }
                      >
                        {/* The link covers the title and price only. It used to wrap
                            the button too, which both clipped it (see
                            .product-card-title) and nested a <button> inside an
                            <a> — the click never reached the button's own handler. */}
                        {/* A nav section can list CATEGORIES as well as products
                            — a variable-amount item like "Telecel Airtime" is a
                            category, and its id is a category id. Sending that
                            to /product/<id> would open an unrelated product, so
                            category items go to /buy/<slug> like everywhere else. */}
                        <Link
                          to={product.href || `/product/${product.id}?category=${product.category_id}`}
                          className="product-card-link"
                        >
                          <div className="product-card-title">{product.title}</div>
                          <div className="product-card-price">{formatPrice(product.price)}</div>
                        </Link>

                        <div className="product-card-action">
                          <Button
                            type="primary"
                            block
                            className="select-options-button"
                            onClick={() => navigate(product.href || `/product/${product.id}?category=${product.category_id}`)}
                          >
                            Buy Now
                          </Button>
                        </div>
                      </Card>
                    </motion.div>
                  </Col>
                ))}
              </Row>
            </motion.div>
          ) : loading ? (
            <div className="loading-container" />
          ) : (
            <Empty description="No products found" className="empty-products" />
          )}
        </Spin>

        <div className="pagination-container">
          {total > pageSize ? (
            <Pagination
              current={currentPage}
              total={total}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              disabled={loading}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ProductListPage

